import { useState } from 'react'
import ReactDOM from 'react-dom'
import Nango from '@nangohq/frontend'
import { useAuth } from '../auth/AuthContext.jsx'
import styles from './ConnectionModal.module.css'

const AUTH_TYPES = [
  { value: 'api_key',  label: 'API Key' },
  { value: 'bearer',   label: 'Bearer Token' },
  { value: 'basic',    label: 'Basic Auth (username + password)' },
  { value: 'oauth2',   label: 'OAuth 2.0' },
]

// ── OAuth flow ─────────────────────────────────────────────────────────────────

function _slugify(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 48)
}

function OAuthPane({ provider, onClose, onConnected, existingSlugs = new Set() }) {
  const { apiFetch } = useAuth()
  const [state, setState] = useState('idle') // idle | connecting | done | error
  const [connParams, setConnParams] = useState({})
  const [connName, setConnName] = useState(provider.name)

  const allParams = provider.connectionParams ?? []
  const requiredParams = allParams.filter(p => !p.optional)
  const paramsComplete = requiredParams.every(p => connParams[p.key]?.trim())
  const slugPreview = _slugify(connName || provider.name)
  const slugConflict = existingSlugs.has(slugPreview)

  const handleAuthorize = async () => {
    setState('connecting')
    try {
      const nangoId = provider.nangoId || provider.id
      const resp = await apiFetch('/api/connectors/nango-session', {
        method: 'POST',
        body: JSON.stringify({ provider: nangoId }),
      })
      if (!resp.ok) throw new Error(`Session request failed: ${resp.status}`)
      const { token } = await resp.json()

      const nango = new Nango({ connectSessionToken: token })
      const filledParams = Object.fromEntries(Object.entries(connParams).filter(([, v]) => v?.trim()))

      // Fields flagged `oauthOverride` (customers bringing their own OAuth app, e.g. NetSuite's
      // per-account Integration Record) go into Nango's per-connection credential override
      // instead of the generic `params` bag Nango uses to build account-specific auth URLs.
      const overrideParams = allParams.filter(p => p.oauthOverride)
      const regularKeys = new Set(allParams.filter(p => !p.oauthOverride).map(p => p.key))
      const params = Object.fromEntries(Object.entries(filledParams).filter(([k]) => regularKeys.has(k)))
      const credentials = Object.fromEntries(
        overrideParams.filter(p => filledParams[p.key]).map(p => [`oauth_${p.oauthOverride}_override`, filledParams[p.key]])
      )

      const authOpts = {}
      if (Object.keys(params).length) authOpts.params = params
      if (Object.keys(credentials).length) authOpts.credentials = credentials

      const result = await nango.auth(nangoId, Object.keys(authOpts).length ? authOpts : undefined)
      const connectionId = result?.connectionId ?? result?.connection_id ?? `${provider.id}-${Date.now()}`
      // Never persist fields marked `secret` (e.g. an OAuth client secret) to our own records —
      // Nango is the only place that stores them once handed off above.
      const persistedParams = Object.fromEntries(
        Object.entries(filledParams).filter(([k]) => !allParams.find(p => p.key === k)?.secret)
      )
      await onConnected(connectionId, provider.id, connName.trim() || provider.name, persistedParams)
      setState('done')
    } catch (err) {
      console.error('Nango auth error:', err)
      setState('error')
    }
  }

  return (
    <div className={styles.pane}>
      <div className={styles.providerHero}>
        <span className={styles.providerIcon}>{provider.icon}</span>
        <div>
          <div className={styles.providerName}>{provider.name}</div>
          <div className={styles.providerCat}>{provider.category}</div>
        </div>
      </div>

      {provider.description && (
        <div className={styles.syncNote}>
          <span className={styles.syncLabel}>Data synced</span>
          <span className={styles.syncText}>{provider.description}</span>
        </div>
      )}

      {provider.setupSteps?.length > 0 && (
        <div className={styles.syncNote}>
          <span className={styles.syncLabel}>Before you connect</span>
          <ol className={styles.setupStepsList}>
            {provider.setupSteps.map((step, i) => <li key={i}>{step}</li>)}
          </ol>
        </div>
      )}

      {state === 'idle' && (
        <>
          <div className={styles.stepBody}>
            <label className={styles.fieldLabel}>Connection name</label>
            <input
              className={styles.fieldInput}
              placeholder={provider.name}
              value={connName}
              onChange={e => setConnName(e.target.value)}
            />
            {slugConflict ? (
              <p className={styles.fieldError}>A connection named &quot;{slugPreview}&quot; already exists. Choose a different name (e.g. &quot;{provider.name} US&quot;).</p>
            ) : (
              <p className={styles.fieldHint}>Used to identify this connection if you connect multiple {provider.name} accounts.</p>
            )}
          </div>
          {allParams.map(p => (
            <div key={p.key} className={styles.stepBody}>
              <label className={styles.fieldLabel}>{p.label}{p.optional ? <span className={styles.optionalTag}> optional</span> : ''}</label>
              <input
                className={styles.fieldInput}
                type={p.secret ? 'password' : 'text'}
                placeholder={p.placeholder}
                value={connParams[p.key] ?? ''}
                onChange={e => setConnParams(prev => ({ ...prev, [p.key]: e.target.value }))}
              />
              {p.hint && <p className={styles.fieldHint}>{p.hint}</p>}
            </div>
          ))}
          <p className={styles.oauthHint}>
            You&apos;ll be taken through {provider.name}&apos;s authorization screen.
            We never store your credentials — only the access token issued by {provider.name}.
          </p>
          <button className={styles.authorizeBtn} onClick={handleAuthorize} disabled={!paramsComplete || slugConflict}>
            Authorize with {provider.name} →
          </button>
        </>
      )}

      {state === 'connecting' && (
        <div className={styles.statusBox}>
          <div className={styles.spinner} />
          <span>Waiting for authorization…</span>
        </div>
      )}

      {state === 'done' && (
        <div className={`${styles.statusBox} ${styles.statusSuccess}`}>
          ✓ Connected — your first sync will begin shortly.
          <button className={styles.closeAfterBtn} onClick={onClose}>Done</button>
        </div>
      )}

      {state === 'error' && (
        <div className={`${styles.statusBox} ${styles.statusError}`}>
          Authorization failed. Try again or use the chat for help.
          <button className={styles.retryBtn} onClick={() => setState('idle')}>Retry</button>
        </div>
      )}
    </div>
  )
}

// ── Custom REST flow ───────────────────────────────────────────────────────────

function RestPane({ provider, onClose, onChatFallback, onConnected }) {
  const { apiFetch } = useAuth()
  const [step, setStep]       = useState(0)  // 0: url, 1: auth, 2: credentials
  const [baseUrl, setBaseUrl] = useState('')
  const [authType, setAuthType] = useState('api_key')
  const [creds, setCreds]     = useState({ apiKey: '', username: '', password: '', token: '' })
  const [extraFields, setExtraFields] = useState(() =>
    Object.fromEntries((provider.restCredentialFields || []).map(f => [f.key, '']))
  )
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [saving, setSaving]   = useState(false)

  const handleTest = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const res = await apiFetch('/api/connectors/test', {
        method: 'POST',
        body: JSON.stringify({ baseUrl, authType, creds }),
      })
      const data = await res.json()
      setTestResult(data)
    } catch (err) {
      setTestResult({ ok: false, error: err.message })
    } finally {
      setTesting(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const connectionId = `${provider.id}-${Date.now()}`
      const restCredentials = {
        base_url: baseUrl.trim(),
        username: creds.username || '',
        password: creds.password || creds.apiKey || creds.token || '',
        ...extraFields,
      }
      await onConnected(connectionId, provider.id, provider.name, {}, restCredentials)
      onClose()
    } catch (err) {
      console.error('Save REST connector error:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles.pane}>
      <div className={styles.providerHero}>
        <span className={styles.providerIcon}>{provider.icon}</span>
        <div>
          <div className={styles.providerName}>{provider.name}</div>
          <div className={styles.providerCat}>{provider.category}</div>
        </div>
      </div>

      <div className={styles.steps}>
        {['Endpoint', 'Auth Type', 'Credentials'].map((s, i) => (
          <div key={s} className={`${styles.step} ${step === i ? styles.stepActive : ''} ${step > i ? styles.stepDone : ''}`}>
            <span className={styles.stepNum}>{step > i ? '✓' : i + 1}</span>
            <span className={styles.stepLabel}>{s}</span>
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className={styles.stepBody}>
          <label className={styles.fieldLabel}>Base URL</label>
          <input
            className={styles.fieldInput}
            placeholder="https://api.yourcompany.com"
            value={baseUrl}
            onChange={e => setBaseUrl(e.target.value)}
            autoFocus
          />
          <p className={styles.fieldHint}>The root URL of the REST API — we&apos;ll append endpoint paths to this.</p>
          <div className={styles.stepActions}>
            <button
              className={styles.nextBtn}
              disabled={!baseUrl.trim()}
              onClick={() => setStep(1)}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className={styles.stepBody}>
          <label className={styles.fieldLabel}>Authentication Type</label>
          <div className={styles.radioGroup}>
            {AUTH_TYPES.map(a => (
              <label key={a.value} className={styles.radioLabel}>
                <input
                  type="radio"
                  value={a.value}
                  checked={authType === a.value}
                  onChange={() => setAuthType(a.value)}
                />
                {a.label}
              </label>
            ))}
          </div>
          <div className={styles.stepActions}>
            <button className={styles.backBtn} onClick={() => setStep(0)}>← Back</button>
            <button className={styles.nextBtn} onClick={() => setStep(2)}>Next →</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className={styles.stepBody}>
          <label className={styles.fieldLabel}>Credentials</label>

          {authType === 'api_key' && (
            <input
              className={styles.fieldInput}
              type="password"
              placeholder="API Key"
              value={creds.apiKey}
              onChange={e => setCreds(c => ({ ...c, apiKey: e.target.value }))}
              autoFocus
            />
          )}
          {authType === 'bearer' && (
            <input
              className={styles.fieldInput}
              type="password"
              placeholder="Bearer Token"
              value={creds.token}
              onChange={e => setCreds(c => ({ ...c, token: e.target.value }))}
              autoFocus
            />
          )}
          {(authType === 'basic' || authType === 'oauth2') && (
            <>
              <input
                className={styles.fieldInput}
                placeholder="Username / Client ID"
                value={creds.username}
                onChange={e => setCreds(c => ({ ...c, username: e.target.value }))}
                autoFocus
              />
              <input
                className={`${styles.fieldInput} ${styles.fieldInputTop}`}
                type="password"
                placeholder="Password / Client Secret"
                value={creds.password}
                onChange={e => setCreds(c => ({ ...c, password: e.target.value }))}
              />
            </>
          )}

          {(provider.restCredentialFields || []).map(f => (
            <div key={f.key}>
              <input
                className={`${styles.fieldInput} ${styles.fieldInputTop}`}
                placeholder={f.placeholder || f.label}
                value={extraFields[f.key] || ''}
                onChange={e => setExtraFields(v => ({ ...v, [f.key]: e.target.value }))}
              />
              {f.hint && <p className={styles.fieldHint}>{f.hint}</p>}
            </div>
          ))}

          {testResult?.ok === true && (
            <div className={`${styles.statusBox} ${styles.statusSuccess}`}>
              ✓ Connection verified ({testResult.status}) — ready to save.
            </div>
          )}
          {testResult?.ok === false && (
            <div className={`${styles.statusBox} ${styles.statusError}`}>
              ✗ {testResult.error || `HTTP ${testResult.status} ${testResult.statusText}`}
            </div>
          )}

          <p className={styles.fieldHint}>
            Credentials are encrypted and stored in AWS Secrets Manager. They are never exposed after saving.
          </p>

          <div className={styles.stepActions}>
            <button className={styles.backBtn} onClick={() => setStep(1)}>← Back</button>
            <button
              className={styles.testBtn}
              onClick={handleTest}
              disabled={testing}
            >
              {testing ? 'Testing…' : 'Test Connection'}
            </button>
            {testResult?.ok && (
              <button className={styles.nextBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save & Connect'}
              </button>
            )}
          </div>
        </div>
      )}

      <button
        className={styles.chatLink}
        onClick={() => { onClose(); onChatFallback(`Help me connect a custom REST API at ${baseUrl || 'my endpoint'}`) }}
      >
        Need help? Ask the AI →
      </button>
    </div>
  )
}

// ── Database pane ───────────────────────────────────────────────────────────────

const _DB_DEFAULT_PORTS = { postgres: 5432, mysql: 3306, sqlserver: 1433 }

function DbPane({ provider, onClose, onChatFallback, onConnected, existingSlugs = new Set() }) {
  const { apiFetch } = useAuth()
  const [step, setStep] = useState(0) // 0: connection details, 1: schema review
  const [connName, setConnName] = useState(provider.name)
  const [host, setHost] = useState('')
  const [port, setPort] = useState(String(_DB_DEFAULT_PORTS[provider.id] || ''))
  const [database, setDatabase] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [introspecting, setIntrospecting] = useState(false)
  const [tables, setTables] = useState([])
  const [selected, setSelected] = useState(new Set())
  const [saving, setSaving] = useState(false)

  const slugPreview = _slugify(connName || provider.name)
  const slugConflict = existingSlugs.has(slugPreview)
  const detailsComplete = host.trim() && database.trim() && username.trim() && password.trim() && !slugConflict

  const dbPayload = {
    engine: provider.id,
    host: host.trim(),
    port: Number(port) || _DB_DEFAULT_PORTS[provider.id],
    database: database.trim(),
    username: username.trim(),
    password,
  }

  const handleTest = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const res = await apiFetch('/api/connectors/database/test', { method: 'POST', body: JSON.stringify(dbPayload) })
      const data = await res.json()
      setTestResult(data)
    } catch (err) {
      setTestResult({ ok: false, error: err.message })
    } finally {
      setTesting(false)
    }
  }

  const handleIntrospect = async () => {
    setIntrospecting(true)
    try {
      const res = await apiFetch('/api/connectors/database/introspect', { method: 'POST', body: JSON.stringify(dbPayload) })
      const data = await res.json()
      const fetchedTables = data.tables || []
      setTables(fetchedTables)
      setSelected(new Set(fetchedTables.filter(t => t.recommended).map(t => t.name)))
      setStep(1)
    } catch (err) {
      setTestResult({ ok: false, error: err.message })
    } finally {
      setIntrospecting(false)
    }
  }

  const toggleTable = (name) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const connectionId = `${provider.id}-${Date.now()}`
      await onConnected(
        connectionId,
        provider.id,
        connName.trim() || provider.name,
        { selected_tables: Array.from(selected) },
        dbPayload,
      )
      onClose()
    } catch (err) {
      console.error('Save database connector error:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles.pane}>
      <div className={styles.providerHero}>
        <span className={styles.providerIcon}>{provider.icon}</span>
        <div>
          <div className={styles.providerName}>{provider.name}</div>
          <div className={styles.providerCat}>{provider.category}</div>
        </div>
      </div>

      {provider.description && (
        <div className={styles.syncNote}>
          <span className={styles.syncLabel}>Data synced</span>
          <span className={styles.syncText}>{provider.description}</span>
        </div>
      )}

      <div className={styles.steps}>
        {['Connection', 'Review Schema'].map((s, i) => (
          <div key={s} className={`${styles.step} ${step === i ? styles.stepActive : ''} ${step > i ? styles.stepDone : ''}`}>
            <span className={styles.stepNum}>{step > i ? '✓' : i + 1}</span>
            <span className={styles.stepLabel}>{s}</span>
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className={styles.stepBody}>
          <label className={styles.fieldLabel}>Connection name</label>
          <input className={styles.fieldInput} placeholder={provider.name} value={connName} onChange={e => setConnName(e.target.value)} />
          {slugConflict && (
            <p className={styles.fieldError}>A connection named &quot;{slugPreview}&quot; already exists. Choose a different name.</p>
          )}

          <label className={styles.fieldLabel}>Host</label>
          <input className={styles.fieldInput} placeholder="db.yourcompany.com" value={host} onChange={e => setHost(e.target.value)} />

          <label className={styles.fieldLabel}>Port</label>
          <input className={styles.fieldInput} placeholder={String(_DB_DEFAULT_PORTS[provider.id] || '')} value={port} onChange={e => setPort(e.target.value)} />

          <label className={styles.fieldLabel}>Database</label>
          <input className={styles.fieldInput} value={database} onChange={e => setDatabase(e.target.value)} />

          <label className={styles.fieldLabel}>Username</label>
          <input className={styles.fieldInput} value={username} onChange={e => setUsername(e.target.value)} />

          <label className={styles.fieldLabel}>Password</label>
          <input className={styles.fieldInput} type="password" value={password} onChange={e => setPassword(e.target.value)} />

          {testResult?.ok === true && (
            <div className={`${styles.statusBox} ${styles.statusSuccess}`}>✓ Connection verified — ready to review schema.</div>
          )}
          {testResult?.ok === false && (
            <div className={`${styles.statusBox} ${styles.statusError}`}>✗ {testResult.error || 'Connection failed.'}</div>
          )}

          <p className={styles.fieldHint}>
            Credentials are encrypted and stored in AWS Secrets Manager. They are never exposed after saving.
          </p>

          <div className={styles.stepActions}>
            <button className={styles.testBtn} onClick={handleTest} disabled={!detailsComplete || testing}>
              {testing ? 'Testing…' : 'Test Connection'}
            </button>
            {testResult?.ok && (
              <button className={styles.nextBtn} onClick={handleIntrospect} disabled={introspecting}>
                {introspecting ? 'Reviewing schema…' : 'Review Schema with AI →'}
              </button>
            )}
          </div>
        </div>
      )}

      {step === 1 && (
        <div className={styles.stepBody}>
          <div className={styles.tableListHeader}>
            <span className={styles.tableListSummary}>{selected.size} of {tables.length} tables selected</span>
            <div className={styles.tableListActions}>
              <button className={styles.tableListActionBtn} onClick={() => setSelected(new Set(tables.map(t => t.name)))}>Select all</button>
              <button className={styles.tableListActionBtn} onClick={() => setSelected(new Set())}>Select none</button>
            </div>
          </div>

          <div className={styles.tableList}>
            {tables.map(t => (
              <label key={t.name} className={styles.tableRow}>
                <input
                  type="checkbox"
                  className={styles.tableRowCheckbox}
                  checked={selected.has(t.name)}
                  onChange={() => toggleTable(t.name)}
                />
                <div className={styles.tableRowBody}>
                  <div className={styles.tableRowTop}>
                    <span className={styles.tableRowName}>{t.name}</span>
                    <span className={styles.tableRowCount}>
                      {t.row_count_estimate != null ? `~${t.row_count_estimate.toLocaleString()} rows` : ''}
                    </span>
                  </div>
                  {t.reason && <span className={styles.tableRowReason}>{t.reason}</span>}
                </div>
              </label>
            ))}
          </div>

          <div className={styles.stepActions}>
            <button className={styles.backBtn} onClick={() => setStep(0)}>← Back</button>
            <button className={styles.nextBtn} onClick={handleSave} disabled={saving || selected.size === 0}>
              {saving ? 'Saving…' : `Save & Connect (${selected.size} table${selected.size === 1 ? '' : 's'})`}
            </button>
          </div>
        </div>
      )}

      <button
        className={styles.chatLink}
        onClick={() => { onClose(); onChatFallback(`Help me connect a ${provider.name} database`) }}
      >
        Need help? Ask the AI →
      </button>
    </div>
  )
}

// ── Modal shell ────────────────────────────────────────────────────────────────

export default function ConnectionModal({ provider, isOpen, onClose, onChatFallback, onConnected, existingSlugs = new Set() }) {
  if (!isOpen || !provider) return null

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <button className={styles.closeBtn} onClick={onClose}>✕</button>
        {provider.authType === 'rest'
          ? <RestPane  provider={provider} onClose={onClose} onChatFallback={onChatFallback} onConnected={onConnected} />
          : provider.authType === 'database'
            ? <DbPane  provider={provider} onClose={onClose} onChatFallback={onChatFallback} onConnected={onConnected} existingSlugs={existingSlugs} />
            : <OAuthPane provider={provider} onClose={onClose} onConnected={onConnected} existingSlugs={existingSlugs} />
        }
      </div>
    </div>,
    document.body
  )
}
